import { experimentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { INCLUDE_SOURCE_SHIPPING_PARAM } from '~/utils/source-shipping';

import type {
  ContentScoringQualityScoreResponse,
  ContentScoringResultsResponse,
  ContentScoringStatusResponse,
  ContentScoringTriggerPayload,
  ContentScoringTriggerResponse,
  ExperimentPipelineApprovalPayload,
  ExperimentPipelineEnrichedProductsResponse,
  ExperimentPipelineQualityScoreResponse,
  ExperimentPipelineStatusResponse,
  ExperimentPipelineTriggerPayload,
  ExperimentPipelineTriggerResponse,
  SampleEnrichmentPreviewResponse,
  SampleEnrichmentQualityScoreResponse,
  SampleEnrichmentStatusResponse,
  SampleEnrichmentTriggerPayload,
  SampleEnrichmentTriggerResponse,
} from '../types/content-scoring.types';
import { fetchAllEnrichedProductPages } from '../utils/fetch-all-enriched-product-pages';

/**
 * Kick off the content-scoring workflow for a CREATED experiment. Optional
 * `payload.subBatchSize` overrides the server env-bound default for the
 * scoring fan-out batch cap; absent falls back to the server default (10).
 * Returns the workflow + run handles plus an initial status.
 */
export async function triggerContentScoring(
  expId: string,
  payload?: ContentScoringTriggerPayload,
): Promise<ContentScoringTriggerResponse> {
  const hasBody = payload != null && Object.keys(payload).length > 0;
  return experimentsClient
    .post(
      endpoints.experiments.contentScoringTrigger(expId),
      hasBody ? { json: payload } : undefined,
    )
    .json<ContentScoringTriggerResponse>();
}

/**
 * Snapshot of the running content-scoring workflow. The funnel polls this
 * until `scoringStatus` flips to a terminal value (`COMPLETED` / `FAILED`);
 * polling cadence lives in the consuming hook, not here.
 */
export async function fetchContentScoringStatus(
  expId: string,
): Promise<ContentScoringStatusResponse> {
  return experimentsClient
    .get(endpoints.experiments.contentScoringStatus(expId))
    .json<ContentScoringStatusResponse>();
}

/**
 * Aggregate quality scores from the latest content-scoring run. Drives the
 * Catalog Content Quality Score gauge and the per-attribute Product
 * Content Quality Score bar chart on the Current Content Score page.
 * Returns `null`-valued fields when scoring has never run for the
 * experiment (see `productCount === 0` empty-state contract).
 */
export async function fetchContentScoringQualityScore(
  expId: string,
): Promise<ContentScoringQualityScoreResponse> {
  return experimentsClient
    .get(endpoints.experiments.contentScoringQualityScore(expId))
    .json<ContentScoringQualityScoreResponse>();
}

/**
 * Kick off the sample-enrichment workflow. Body is optional — when omitted
 * the server defaults `sampleSize=10` and derives `useCase` from the
 * experiment's `adPlatform` + `contentAttributes` (current call site relies
 * on this). Pass a payload to override sample size or restrict GMC fields.
 */
export async function triggerSampleEnrichment(
  expId: string,
  payload?: SampleEnrichmentTriggerPayload,
): Promise<SampleEnrichmentTriggerResponse> {
  const hasBody = payload != null && Object.keys(payload).length > 0;
  return experimentsClient
    .post(
      endpoints.experiments.sampleEnrichmentTrigger(expId),
      hasBody ? { json: payload } : undefined,
    )
    .json<SampleEnrichmentTriggerResponse>();
}

/**
 * Snapshot of the running sample-enrichment workflow. Polled in parallel
 * with content-scoring status while the funnel's combined-progress screen
 * is up.
 */
export async function fetchSampleEnrichmentStatus(
  expId: string,
): Promise<SampleEnrichmentStatusResponse> {
  return experimentsClient
    .get(endpoints.experiments.sampleEnrichmentStatus(expId))
    .json<SampleEnrichmentStatusResponse>();
}

/**
 * Aggregate Before/After/Lift quality scores for the latest sample-
 * enrichment run. Drives the comparison card + per-attribute bar chart
 * on the Review & Refine "Quality Score" tab.
 */
export async function fetchSampleEnrichmentQualityScore(
  expId: string,
): Promise<SampleEnrichmentQualityScoreResponse> {
  return experimentsClient
    .get(endpoints.experiments.sampleEnrichmentQualityScore(expId))
    .json<SampleEnrichmentQualityScoreResponse>();
}

/**
 * Paginated per-product scoring results from the latest content-scoring
 * run. Drives the "Preview Products" tab on the Enrichment Overview page.
 * `page` is zero-based; columns are derived dynamically from `field_scores`.
 */
export async function fetchContentScoringResults(
  expId: string,
  params: { page: number; size: number },
): Promise<ContentScoringResultsResponse> {
  return experimentsClient
    .get(endpoints.experiments.contentScoringResults(expId), {
      searchParams: { page: params.page, size: params.size },
    })
    .json<ContentScoringResultsResponse>();
}

/**
 * Paginated per-product preview cards for the latest sample-enrichment
 * run. Drives the table on the Review & Refine "Preview Products" tab.
 * `page` is zero-based.
 */
export async function fetchSampleEnrichmentPreview(
  expId: string,
  params: { page: number; size: number },
): Promise<SampleEnrichmentPreviewResponse> {
  return experimentsClient
    .get(endpoints.experiments.sampleEnrichmentPreview(expId), {
      searchParams: { page: params.page, size: params.size, ...INCLUDE_SOURCE_SHIPPING_PARAM },
    })
    .json<SampleEnrichmentPreviewResponse>();
}

/**
 * Kick off the end-to-end experiment pipeline (split → curation →
 * enrichment → delivery). Body is empty — all configuration is read
 * server-side from the experiment record. Pre-conditions: experiment
 * must have a non-empty `trafficSplit`, and no pipeline can already be
 * IN_PROGRESS for this experiment (both checked by the backend).
 */
export async function triggerExperimentPipeline(
  expId: string,
  payload?: ExperimentPipelineTriggerPayload,
): Promise<ExperimentPipelineTriggerResponse> {
  const hasBody = payload != null && Object.keys(payload).length > 0;
  return experimentsClient
    .post(endpoints.experiments.pipelineTrigger(expId), hasBody ? { json: payload } : undefined)
    .json<ExperimentPipelineTriggerResponse>();
}

/**
 * Snapshot of the running experiment pipeline. Per the contract there
 * is **no per-stage status** — just one overall IN_PROGRESS /
 * AWAITING_APPROVAL / COMPLETED / FAILED state plus timestamps. Polled
 * until terminal; cadence lives in the consuming hook.
 */
export async function fetchExperimentPipelineStatus(
  expId: string,
): Promise<ExperimentPipelineStatusResponse> {
  return experimentsClient
    .get(endpoints.experiments.pipelineStatus(expId))
    .json<ExperimentPipelineStatusResponse>();
}

/**
 * Aggregate Before/After/Lift + per-attribute + per-category quality scores
 * for the latest full-catalog pipeline run. Drives the Enrichment Overview
 * page; distinct from `/content-scoring/quality-score` which only returns
 * the post-enrichment catalog score.
 */
export async function fetchExperimentPipelineQualityScore(
  expId: string,
): Promise<ExperimentPipelineQualityScoreResponse> {
  return experimentsClient
    .get(endpoints.experiments.pipelineQualityScore(expId))
    .json<ExperimentPipelineQualityScoreResponse>();
}

/**
 * Paginated enriched products from the completed experiment pipeline run.
 * Drives the Enrichment Overview "Preview Products" tab. `page` is zero-based.
 */
export async function fetchExperimentPipelineEnrichedProducts(
  expId: string,
  params: { page: number; size: number },
): Promise<ExperimentPipelineEnrichedProductsResponse> {
  return experimentsClient
    .get(endpoints.experiments.pipelineEnrichedProducts(expId), {
      searchParams: { page: params.page, size: params.size, ...INCLUDE_SOURCE_SHIPPING_PARAM },
    })
    .json<ExperimentPipelineEnrichedProductsResponse>();
}

/**
 * Every enriched product of the completed experiment pipeline run, across all
 * server pages (`size` is capped at 200). The read spans both enrichment scopes
 * (sample ∪ pipeline, deduplicated server-side on `externalId`). Backs the
 * Enrichment Overview listing, which filters and paginates client-side.
 */
export async function fetchAllExperimentPipelineEnrichedProducts(
  expId: string,
): Promise<ExperimentPipelineEnrichedProductsResponse> {
  return fetchAllEnrichedProductPages((page, size) =>
    fetchExperimentPipelineEnrichedProducts(expId, { page, size }),
  );
}

/**
 * Records the operator's APPROVED/REJECTED decision while the pipeline is
 * paused at `AWAITING_APPROVAL`. Server returns 2xx with no body on the
 * first call; subsequent calls return 409 Conflict (one-shot signal).
 */
export async function submitExperimentPipelineApproval(
  expId: string,
  payload: ExperimentPipelineApprovalPayload,
): Promise<void> {
  await experimentsClient.post(endpoints.experiments.pipelineApproval(expId), { json: payload });
}
